import { createContext, type ReactNode, useContext, useMemo } from 'react'
import type { Clients } from '../utils/useCustomizationByURLParams'
import useCustomizationByURLParams, {
  resolveCustomizeClient,
} from '../utils/useCustomizationByURLParams'

type ClientContextProps = {
  /** 'app' (Capacitor/PWA), 'jobka'/'iframe' (embedded), or null (plain web) */
  client: Clients | null
}

const ClientContext = createContext<ClientContextProps>({ client: null })

// https://stackoverflow.com/a/52695341/6544122
export const isPurePWA = () =>
  Boolean(
    window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error Standalone
      window.navigator['standalone'] ||
      document.referrer.includes('android-app://') ||
      document.referrer.includes('capacitor://') ||
      window.navigator.userAgent.includes('FaceUpApp')
  )

type ClientProviderProps = {
  children: ReactNode
}

/**
 * Resolves the client kind ONCE, synchronously, at mount - no setter, nothing
 * below the router mutates it during startup. A `?client=jobka|iframe` URL param
 * (persisted to sessionStorage) wins; otherwise a standalone/Capacitor context is
 * the app; otherwise plain web.
 */
export const ClientProvider = ({ children }: ClientProviderProps) => {
  const { customizeClient } = useCustomizationByURLParams(window.location.search)

  const client = useMemo<Clients | null>(
    () => customizeClient() ?? (isPurePWA() ? 'app' : null),
    [customizeClient]
  )

  return <ClientContext.Provider value={{ client }}>{children}</ClientContext.Provider>
}

export const useClient = () => useContext(ClientContext)

/**
 * Pure client-kind resolution (no React), mirroring the provider's logic, so
 * non-component code (e.g. the Apollo JWT lookup) can pick the matching storage.
 */
export const resolveClientKind = (): Clients | null =>
  resolveCustomizeClient() ?? (isPurePWA() ? 'app' : null)
